import { NearestMinutes } from 'date-fns';
import { AsyncProviderConfig, LDMultiKindContext } from 'launchdarkly-react-client-sdk';

import { DiagnosticMessageType } from './diagnosticMessages/types';
import { LocationSummary } from './types/location';

export default class ooeConstants {
  static get CYPRESS_LD_DEFAULTS_STORAGE_KEY() {
    return 'cypress.ld.flags.defaults';
  }

  static get CYPRESS_LD_OVERRIDES_STORAGE_KEY() {
    return 'cypress.ld.flags.overrides';
  }

  static get CYPRESS_LD_FLAGS(): {
    defaults: Record<string, unknown>;
    overrides: Record<string, unknown>;
  } {
    if (!this.IS_IN_CYPRESS_TEST) {
      return {
        defaults: {},
        overrides: {},
      };
    }

    const readFlags = (storageKey: string): Record<string, unknown> => {
      const value = window.localStorage.getItem(storageKey);
      if (!value) {
        return {};
      }

      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      } catch {
        return {};
      }
    };

    return {
      defaults: readFlags(this.CYPRESS_LD_DEFAULTS_STORAGE_KEY),
      overrides: readFlags(this.CYPRESS_LD_OVERRIDES_STORAGE_KEY),
    };
  }

  static get CYPRESS_LD_BOOTSTRAP(): Record<string, unknown> | undefined {
    if (!this.IS_IN_CYPRESS_TEST) {
      return undefined;
    }

    const { defaults, overrides } = this.CYPRESS_LD_FLAGS;
    const merged = { ...defaults, ...overrides };

    return Object.keys(merged).length > 0 ? merged : undefined;
  }

  static get APP_VERSION() {
    const version = import.meta?.env?.VITE_APP_VERSION ?? '0.0.0';
    return `${version}${import.meta.env.DEV ? '-development' : ''}`;
  }

  static get MOCK_SERVER() {
    return import.meta?.env?.VITE_MOCK_SERVER === 'true';
  }

  static get AMPLITUDE_ENABLED() {
    if (this.IS_IN_CYPRESS_TEST) {
      return false;
    }

    if (import.meta.env.PROD) {
      return true;
    }

    const enableAmplitudeInDevelopment =
      import.meta.env?.VITE_APP_ENABLE_AMPLITUDE_IN_DEVELOPMENT?.toLowerCase?.() === 'true';
    return !!enableAmplitudeInDevelopment;
  }

  static get AMPLITUDE_API_KEY() {
    const forProduction = '554e50c35570b7810cca7102965aed76';
    const forDevAndQa = 'cb755829118203626e473680b9436912';

    if (import.meta.env.DEV || window.location?.host?.startsWith('localhost')) {
      return forDevAndQa;
    }

    if (window.location?.hostname?.startsWith('dev.')) {
      return forDevAndQa;
    }

    if (window.location?.hostname?.startsWith('qa.')) {
      return forDevAndQa;
    }

    return forProduction;
  }

  static get LOGGING_ENABLED() {
    if (this.IS_IN_CYPRESS_TEST) {
      return false;
    }

    if (import.meta.env.PROD) {
      return true;
    }

    const enableBugsnagInDevelopment =
      import.meta.env?.VITE_APP_ENABLE_BUGSNAG_IN_DEVELOPMENT?.toLowerCase?.() === 'true';
    return !!enableBugsnagInDevelopment;
  }

  static get BUGSNAG_RELEASE_STAGE() {
    if (import.meta.env.DEV || window.location?.host?.startsWith('localhost')) {
      return this.DEVELOPER;
    }

    switch (this.ENV) {
      case this.DEV:
        return this.BUGSNAG_DEV_DEPLOY;
      case this.PROD:
        return ooeConstants.PRODUCTION;
      default:
        return this.QA;
    }
  }

  // this exists to resolve the conflict with Bugsnag using 'development'
  // as default release stage - we have a development deploy environment
  // which is what this represents
  static get BUGSNAG_DEV_DEPLOY() {
    return 'dev';
  }

  static get BUGSNAG_API_KEY() {
    return 'b0d00dfe57549e290ed0ba83afe6551e';
  }

  static get DATADOG_APP_ID() {
    return '19c34b4f-a5b8-4aa2-b61d-6a9ab0a712fc';
  }

  static get DATADOG_CLIENT_TOKEN() {
    return 'pubc0e11d5571f3fe4a4ec6eb7c9b76eb86';
  }

  static get DATADOG_SERVICE_NAME() {
    return 'rex-catering-app';
  }

  static get DELIVERY(): 'Delivery' {
    return 'Delivery';
  }

  static get PICKUP(): 'Pickup' {
    return 'Pickup';
  }

  static get DAY_PART_BREAKFAST() {
    return 'Breakfast';
  }

  static get OFFLINE_MESSAGE() {
    return 'Unable to connect to the internet. Please check your network and try again.';
  }

  static get ONLINE_MESSAGE() {
    return 'Your connection has been reestablished';
  }

  static get ONLINE_MESSAGE_TIMEOUT() {
    return 5000;
  }

  static get DISMISSIBLE() {
    return '[DISMISSIBLE]';
  }

  static get TOGGLEABLE_ITEM_TAGS() {
    return [
      'BACON',
      'CHEESE_COLBY_JACK',
      'PICKLES',
      'MULTIGRAIN_BUN',
      'CHEESE_AMERICAN',
      'CHEESE_PEPPER_JACK',
      'FRUIT_CUP_MEDIUM',
      'SUPERFOOD_SIDE_SMALL',
      'MONTEREY_SHRED',
      'BLUE_CHEESE',
      'BACON_CRUMB',
      'CHOP_EGG',
    ];
  }

  static get DEFAULT_AVAILABLE_MODIFIER_TYPES() {
    return ['RECIPE', 'NO_REFUND_RECIPE'];
  }

  static get SAUCES() {
    return [
      '8OZ_BARBEQUE_SAUCE',
      '8OZ_POLYNESIAN',
      '8OZ_BUFFALO_SAUCE',
      '8OZ_RANCH',
      '8OZ_HONEY_MUSTARD',
      '8OZ_CFA_SAUCE',
      '8OZ_SRIRACHA',
    ];
  }

  static get DEVELOPER() {
    return 'developer';
  }

  static get DEVELOPMENT() {
    return 'development';
  }

  static get PRODUCTION() {
    return 'production';
  }

  static get LOCAL() {
    return 'local';
  }

  static get DEV() {
    return 'dev';
  }

  static get QA() {
    return 'qa';
  }

  static get PROD() {
    return 'prod';
  }

  static get IS_IN_CYPRESS_TEST() {
    return !!window.Cypress;
  }

  static get CREDIT() {
    return 'Stored Payment';
  }

  static get DEFER() {
    return 'Pay Later';
  }

  static get REQUEST_PAYMENT() {
    return 'New Payment';
  }

  static get PAYMENT_PENDING() {
    return 'PaymentPending';
  }

  static get ERROR() {
    return 'Error';
  }

  static get FINALIZED() {
    return 'Finalized';
  }

  static get READY() {
    return 'Ready';
  }

  static get CANCELLED() {
    return ['Cancelled', 'Cancelled-Web'];
  }

  static get UTC_TIME() {
    return 'utcTime';
  }

  static get ZERO_DOLLARS() {
    return '$0.00';
  }

  static get DEFAULT_ENVIRONMENT() {
    return 'qa';
  }

  static get POS_ORDER_WARNING_TEXT() {
    return 'If this order was recently entered on CMT, there may be a delay in the order appearing. Please allow the system to correct itself on the following business day.';
  }

  static get READY_ORDER_WARNING_TEXT() {
    return 'This order is active in the Restaurant and cannot be edited from CMT. Please use the POS for any subsequent edits or cancellations.';
  }

  static get ENVIRONMENT_DATA() {
    const { hostname } = window.location;
    let environment = this.DEFAULT_ENVIRONMENT;
    let pilotFlag = '';
    let pilot = false;

    const rx = /(qa|dev|prod)?(-pilot)?\.?(my|order-management|crndev)\.?(chick-fil-a|cfahome)\.com.*/g;
    const getEnv = rx.exec(hostname);

    if (hostname === 'localhost') {
      environment = this.LOCAL;
    }
    if (getEnv) {
      [, environment, pilotFlag] = getEnv;
      pilot = pilotFlag === '-pilot';
      environment = environment || 'prod';
    }
    return { ENV: environment, IS_PILOT: pilot };
  }

  static get ENV() {
    return this.ENVIRONMENT_DATA.ENV;
  }

  static get OKTA_INVALID_ERROR() {
    return 'access_denied';
  }

  static get EARLIEST_AVAILABLE_BREAKFAST() {
    return 'earliestAvailableBreakfast';
  }

  static get EARLIEST_AVAILABLE() {
    return 'earliestAvailable';
  }

  static get OKTA_CONFIG() {
    const qa = {
      url: 'https://cfahome.oktapreview.com',
      clientId: '0oae1cb28rKPvxpzc0h7',
      redirectUri: `${window.location.origin}`,
      issuer: 'https://cfahome.oktapreview.com/oauth2/ausc6e4sj2fnPnQ670h7',
      authorizeUrl: 'https://cfahome.oktapreview.com/oauth2/ausc6e4sj2fnPnQ670h7/v1/authorize',
    };

    const prod = {
      url: 'https://cfahome.com',
      clientId: '0oa622ol2eB8e5PBv1t7',
      redirectUri: `${window.location.origin}`,
      issuer: 'https://cfahome.okta.com/oauth2/aus4i6zex3F52d4rn1t7',
      authorizeUrl: 'https://cfahome.okta.com/oauth2/aus4i6zex3F52d4rn1t7/v1/authorize',
    };

    if (this.ENV === this.PROD) {
      return prod;
    }
    return qa;
  }

  static get OKTA_STORAGE_KEY() {
    return 'cmt-token-storage';
  }

  static get LD_CONFIG(): AsyncProviderConfig {
    const context: LDMultiKindContext = {
      kind: 'multi',
      environment: {
        key: this.ENV,
      },
    };
    const cypressBootstrap = this.CYPRESS_LD_BOOTSTRAP;
    const bootstrap = cypressBootstrap ?? 'localStorage';
    // When Cypress supplies explicit flag values, skip the local ldcli/dev-server
    // so its eval response cannot overwrite the bootstrap (streaming:false alone
    // still allows the initial /sdk/evalx request).
    const cypressOptions = cypressBootstrap
      ? {
          streaming: false as const,
          baseUrl: 'http://127.0.0.1:9',
          streamUrl: 'http://127.0.0.1:9',
          eventsUrl: 'http://127.0.0.1:9',
        }
      : {};

    switch (this.ENV) {
      case this.LOCAL: {
        return {
          clientSideID: 'catering-management-tool',
          context,
          options: {
            bootstrap,
            streamUrl: 'http://localhost:8765',
            baseUrl: 'http://localhost:8765',
            eventsUrl: 'http://localhost:8765',
            ...cypressOptions,
          },
        };
      }
      case this.DEV:
      case this.QA: {
        return {
          clientSideID: '68deb480e36fa509a82e0826', // test env
          timeout: 5, // init timeout of 5 seconds
          context,
          options: {
            bootstrap,
            ...cypressOptions,
          },
        };
      }
      default: {
        return {
          clientSideID: '68deb480e36fa509a82e0827', // prod env
          timeout: 3, // init timeout of 3 seconds
          context,
          options: {
            bootstrap,
            ...cypressOptions,
          },
        };
      }
    }
  }

  /**
   * Local-only backend overrides, configured via VITE_* vars in .env.local (gitignored, never
   * committed -- see README "Local environment configuration"). Both getters below gate on
   * `import.meta.env.DEV`, which Vite sets to true only while running its dev server (`yarn
   * start`, `yarn mock:app`). It's false in every built bundle, including the ones running in the
   * dev/qa/prod deployed environments, so these overrides have no path into a deployed build even
   * if a VITE_* var ended up set somewhere in CI.
   */

  /**
   * Set VITE_SERVICE_TIER=dev in .env.local to point the crndev REST hosts and Connect RPC hosts
   * at the dev tier instead of their non-prod default (qa for REST, test for Connect). Prod hosts
   * are resolved before this is consulted, so it has no effect on a prod build either way.
   */
  static get USE_DEV_SERVICES() {
    return import.meta.env.DEV && import.meta?.env?.VITE_SERVICE_TIER?.toLowerCase?.() === this.DEV;
  }

  /** Tier segment for the crndev REST hosts, which default to qa (api.qa.crndev...). */
  static get SERVICE_TIER() {
    return this.USE_DEV_SERVICES ? this.DEV : this.QA;
  }

  /** Tier segment for the Connect RPC hosts, which default to test (test.d2c.api..., test.rex.api...). */
  static get CONNECT_SERVICE_TIER() {
    return this.USE_DEV_SERVICES ? this.DEV : 'test';
  }

  /**
   * Set VITE_REX_CATERING_URL in .env.local to a full base URL (e.g. http://localhost:8080) to
   * send rex-catering traffic to a locally running instance instead of the deployed host. Include
   * the scheme -- local runs over http, not https. Wins over VITE_SERVICE_TIER when both are set.
   * Gated on `import.meta.env.DEV` for the same reason as USE_DEV_SERVICES above.
   */
  static get REX_CATERING_URL_OVERRIDE() {
    return import.meta.env.DEV ? (import.meta?.env?.VITE_REX_CATERING_URL ?? '') : '';
  }

  static get DXE_SERVICES_BASE() {
    if (this.ENV === this.PROD) {
      return 'api.my.chick-fil-a.com';
    }
    return `api.${this.SERVICE_TIER}.crndev.chick-fil-a.com`;
  }

  static get D2C_SERVICES_BASE() {
    if (this.ENV === this.PROD) {
      return 'api.my.chick-fil-a.com';
    }
    return `api.${this.SERVICE_TIER}.crndev.chick-fil-a.com`;
  }

  static get D2C_CONNECT_SERVICE_BASE() {
    if (this.ENV === this.PROD) {
      return 'd2c.api.chick-fil-a.com';
    }
    return `${this.CONNECT_SERVICE_TIER}.d2c.api.chick-fil-a.com`;
  }

  static get REX_CATERING_BASE() {
    if (this.ENV === this.PROD) {
      return 'rex.api.chick-fil-a.com';
    }
    return `${this.CONNECT_SERVICE_TIER}.rex.api.chick-fil-a.com`;
  }

  static get URL() {
    let pilot = '';
    if (this.ENVIRONMENT_DATA.IS_PILOT) {
      pilot = '-pilot';
    }

    return {
      MENU: this.MOCK_SERVER
        ? 'http://localhost:3001/api/menu'
        : `https://order${pilot}.${this.DXE_SERVICES_BASE}/orders`,
      ORDERS: `https://order${pilot}.${this.DXE_SERVICES_BASE}/orders`,
      LOCATION_ORDERS: `https://order${pilot}.${this.DXE_SERVICES_BASE}/locations`,
      D2C: `https://d2c${pilot}.${this.D2C_SERVICES_BASE}`,
      D2C_CONNECT: `https://${this.D2C_CONNECT_SERVICE_BASE}`,
      PROFILE: `https://profile${pilot}.${this.DXE_SERVICES_BASE}`,
      LOCATIONS: `https://location${pilot}.${this.DXE_SERVICES_BASE}`,
      PAYMENT: `https://gc${pilot}.${this.DXE_SERVICES_BASE}`,
      REX_CATERING: this.REX_CATERING_URL_OVERRIDE || `https://${this.REX_CATERING_BASE}`,
    };
  }

  static get DXE_DELAY_TIME() {
    return 1000;
  }

  static get DXE_DASHBOARD_DELAY_TIME() {
    return 2000;
  }

  static get GUEST_SEARCH_DELAY_TIME() {
    return 500;
  }

  // CIS defaults page_number to 1 when unset. Do not send 0 — that is only the
  // next_page "no further page" default on the response.
  static get FIRST_MEMBER_SEARCH_PAGE() {
    return 1;
  }

  static get MEMBER_SEARCH_DEBOUNCE_TIME() {
    return 400;
  }

  // Typeahead fires on its own at 3 characters. Enter forces a search at 2.
  static get MEMBER_SEARCH_MIN_TYPEAHEAD_LENGTH() {
    return 3;
  }

  static get MEMBER_SEARCH_MIN_SUBMIT_LENGTH() {
    return 2;
  }

  static get ADDRESS_SEARCH_DEBOUNCE_TIME() {
    return 500;
  }

  static get ADDRESS_SEARCH_MIN_TYPEAHEAD_LENGTH() {
    return 3;
  }

  static get ADDRESS_SEARCH_MAX_RESULTS() {
    return 5;
  }

  static get GET_TITLE() {
    return {
      '/': 'Guest',
      '/menu': 'Menu',
      '/event': 'Event Details',
      '/payment': 'Payment',
      '/more': 'More',
      '/dashboard': 'Customer Dashboard',
    };
  }

  static get GET_STATUS() {
    return {
      PaymentPending: 'Payment Pending',
      Ready: 'Payment Received',
      Submit: 'Payment Received',
      Cancelled: 'Cancelled',
      'Cancelled-Web': 'Cancelled',
      PayLater: 'Pay Later',
      Stored: 'Stored (POS)',
      Finalized: 'Tendered (POS)',
      CheckIn: 'Tendered (POS)',
      Cleared: 'Cleared',
      Error: 'Error',
      Refunded: 'Refunded',
      'Cleared - Manual': 'Cleared',
    };
  }

  static GET_EVENT_MESSAGE(order: { displayClient?: Nullable<string>; email?: Nullable<string> }) {
    return {
      create: `Order placed via ${order.displayClient}`,
      paymentPending: `Payment request email sent to ${order.email}`,
      submit: 'Order submitted successfully - in deferred order queue',
      cancelled: 'Order cancelled',
      cancel: 'Order cancelled',
      myPayment: 'Payment details entered by customer - in deferred order queue ',
      error: 'An error occurred during the submission of this order.',
    };
  }

  static get GET_LOYALTY() {
    return {
      'Chick-fil-a One':
        'Earn 10 points for every dollar spent. Earn 1000 points to reach Chick-fil-A One Silver status.',
      Red: 'Earn 12 points for every dollar spent. Receive exclusive benefits including two free tickets to the College Football Hall of Fame in Atlanta, GA.',
      Silver: 'Earn 11 points for every dollar spent. Earn 5000 points to reach Chick-fil-A One Red status.',
      Signature: 'Earn 13 points for every dollar spent.',
    };
  }

  static get GET_CLIENT() {
    return {
      ooe_masquerade: 'Catering Management Tool',
      MyPayments: 'Catering Management Tool',
      CfaDotCom: 'Chick-fil-a.com',
      GuestLogin: 'Testing Automation',
      POS: 'Restaurant Point of Sale',
      PWO: 'Web',
      PWOCA: 'Web',
      PWOGuest: 'Web',
      MobileAppV2: 'Mobile App',
    };
  }

  static get WEB_CLIENT_IDS() {
    return ['PWO', 'PWOCA', 'PWOGuest'] as const;
  }

  static get EMPTY_DASHBOARD_FILTERS() {
    return { type: [] as string[], source: [] as string[], paymentStatus: [] as string[] };
  }

  static get DASHBOARD_SEARCH_DEBOUNCE_TIME() {
    return 500;
  }

  static get DASHBOARD_SEARCH_MIN_LENGTH() {
    return 3;
  }

  static get DASHBOARD_TYPE_OPTIONS() {
    return [this.PICKUP, this.DELIVERY];
  }

  /** Maps each dashboard Source option label to the `displayClient` value of the orders it represents. */
  static get DASHBOARD_SOURCE_DISPLAY_CLIENTS(): Record<string, string> {
    return {
      'Catering Management Tool': this.GET_CLIENT.ooe_masquerade,
      'Restaurant POS': this.GET_CLIENT.POS,
      Web: this.GET_CLIENT.PWO,
    };
  }

  static get DASHBOARD_SOURCE_OPTIONS() {
    return Object.keys(this.DASHBOARD_SOURCE_DISPLAY_CLIENTS);
  }

  static get DASHBOARD_SORT_LABELS() {
    return {
      totalAsc: 'Order Total · Low to High',
      totalDesc: 'Order Total · High to Low',
      sourceAsc: 'Source · A → Z',
      sourceDesc: 'Source · Z → A',
      guestNameAsc: 'Guest Name · A → Z',
      guestNameDesc: 'Guest Name · Z → A',
    };
  }

  static get DASHBOARD_PAYMENT_STATUS_OPTIONS() {
    return [
      this.GET_STATUS.PaymentPending,
      this.GET_STATUS.PayLater,
      this.GET_STATUS.Ready,
      this.GET_STATUS.Error,
      this.GET_STATUS.Cancelled,
    ];
  }

  static get GET_DASHBOARD_CANCEL_MESSAGES() {
    return {
      message: 'Are you sure you want to cancel this order? This cannot be undone.',
      confirmText: 'Yes, cancel this order',
      declineText: "Nevermind, don't cancel",
      cancelled: 'This order has been cancelled.',
    };
  }

  static get GET_DASHBOARD_RESEND_PAYMENT_MESSAGES() {
    return {
      message: 'Are you sure you want to resend payment confirmation email to',
      confirmText: 'Yes, resend email',
      declineText: "Nevermind, don't resend email",
      emailSent: 'Success! Request payment email was sent to',
    };
  }

  static get GET_DASHBOARD_RESEND_EMAIL_MESSAGES() {
    return {
      message: 'Resend email to',
      confirmText: 'Yes, Send Email',
      declineText: "No, Don't Send",
      emailSent: 'Success! Email was sent to',
      emailNotSent: 'Failed to send email to',
    };
  }

  static get GET_DASHBOARD_EDIT_WITH_REWARDS_MESSAGES() {
    return {
      infoMessage:
        'This order contains Spotlight Rewards. Please make all edits on the POS. Editing this order through CMT will remove the Spotlight Reward and return it to the Guest.',
      message: 'Are you sure you want to remove the Reward and edit the order?',
      confirmText: 'Yes, remove and edit',
      declineText: 'Cancel',
    };
  }

  static get GET_PAYMENT_TYPES() {
    return {
      ACCOUNT: 'ACCOUNT',
      TO_BE_COLLECTED: 'TO_BE_COLLECTED',
    };
  }

  static MAILTO_STRING(locationNumber: string) {
    return `mailto:DigitalCateringFeedback@chick-fil-a.com?subject=Feedback from ${locationNumber} on Catering Management Tool`;
  }

  static get HELPFUL_LINKS() {
    return {
      ABOUT_CMT:
        'https://portal.cfahome.com/en/food-and-service/sales-channels/catering/catering-management-tool/',
      USER_GUIDE:
        'https://portal.cfahome.com/globalassets/documents/food-and-service/sales-channels/catering-management-tool-user-guide.pdf?cb=48f04e',
      NEED_HELP: 'https://cfaprod.service-now.com/cfahelp',
      FEEDBACK: 'https://cfacorp.co1.qualtrics.com/jfe/form/SV_bEhzTvqBnxrMP1I',
    };
  }

  static get TOKEN_TYPES() {
    return {
      GUEST_TYPE: 'GUEST_TYPE',
      MASQUERADE_TYPE: 'MASQUERADE_TYPE',
    };
  }

  static get VCA_INKTEL() {
    return 'VCA_INKTEL';
  }

  static get allowedCardTypes() {
    return ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER', 'JCB', 'DINERS'];
  }

  static get paypal() {
    return 'PayPal';
  }

  static get DATE_TIME_FORMAT() {
    return {
      longDate: 'EEEE, MMMM d, h:mm aaa',
      shortDate: 'MM/d/yyyy h:mm aaa',
      eventSummaryDate: 'EEEE, MMM do',
      eventSummaryTime: 'h:mm aaa',
      dashboardOrderTime: 'h:mm aa',
      dashboardOrderDate: 'EEEE, MM/dd/yy',
      accordionDate: 'EEEE, MMMM d, yyyy',
      orderCardDate: 'MMMM d, yyyy',
      readableDate: 'EEEE, MMM d h:mm aaa',
      memberOrderCardDate: 'EEEE, MM/dd/yy, h:mm aaa',
      timeAmPm: 'hh:mm aa',
      dateTime: "yyyy-MM-dd'T'HH:mm:ss",
      time: 'HH:mm',
      date: 'yyyy-MM-dd',
    };
  }

  static get PERMISSIONS() {
    return {
      ADMIN: 'ADMIN',
      VCA: 'VCA',
      VCA_BYPASS: 'VCA_BYPASS',
    };
  }

  static get CONTRACTOR_AUDIENCE() {
    return 'CONTRACTOR_AUDIENCE';
  }

  static get STAFF_AUDIENCE() {
    return 'STAFF_AUDIENCE';
  }

  static get FIND_NUMBER_REGEX() {
    return RegExp('(\\d{1,10})', 'g');
  }

  static get POS_INVALID_CHARS_REGEX() {
    // escape *is* necessary in this case
    // eslint-disable-next-line no-useless-escape
    return /[^\+\-\*\.,?!' a-zA-Z0-9]/g;
  }

  static get EULA(): 'cmtEula' {
    return 'cmtEula';
  }

  static get LOCAL_STORAGE_EULA() {
    return 'localStorageCmtEula';
  }

  static get EULA_MESSAGE() {
    return `<p>The Catering Management Tool is a Chick-fil-A, Inc. platform made available to support Operators' catering orders from order entry to fulfillment. 
    Chick-fil-A, Inc. has collected from customers certain customer information (that includes personal information under certain privacy and data security laws) 
    available to you in the Catering Management Tool.</p>\n \n<p>The Catering Management Tool and the customer information stored by Chick-fil-A, Inc. and available 
    through this platform are proprietary to and the confidential information of Chick-fil-A, Inc. <strong>You are solely responsible for your Chick-fil-A Restaurant(s) 
    and your Team Members' access to and use of the Catering Management Tool and any related customer information. Access should be limited to Operators and employees 
    designated as Catering Leaders due to customer privacy and data security concerns.</strong></p> \n \n<p>As a user of the Catering Management Tool, it is your duty and 
    responsibility to keep this information confidential. You must only use the Catering Management Tool in connection with the sale of Chick-fil-A products within the 
    Chick-fil-A franchise system, and in accordance with all applicable federal and state laws, rules and regulations.</p>\n \n<p><strong>As a reminder, it is your responsibility 
    to keep your Chick-fil-A password secure. Do not share your personal Chick-fil-A password with anyone, even other Team Members. If you think someone has gained access to your 
    password, change it. If you need help changing your password, please call the IT Helpdesk.</strong></p>`;
  }

  static get NUM_ADDTL_PAST_ORDERS_TO_LOAD() {
    return 3;
  }

  static get NUM_ADDTL_DASHBOARD_PAST_ORDERS_TO_LOAD() {
    return 1;
  }

  static get NUM_OF_PAST_ORDERS_FOR_DELIVERY_ADDRESSES() {
    return 20;
  }

  static get ADDRESS_DROPDOWN_Z_INDEX() {
    return 1200;
  }

  static get NO_PICKUP_LEAD_TIME() {
    return 'No pickup lead time defined for this location';
  }

  static get NO_DELIVERY_LEAD_TIME() {
    return 'No delivery lead time defined for this location';
  }

  static get LOCAL_STORAGE_DEFAULT_LOCATION() {
    return 'storedLocationNumber';
  }

  static get BUGSNAG_ERRORCLASS_PAYMENTS() {
    return 'Payments API';
  }

  static get BUGSNAG_ERRORCLASS_ORDERS() {
    return 'Orders API';
  }

  static get BUGSNAG_ERRORCLASS_PROFILE() {
    return 'Profile API';
  }

  static get BUGSNAG_ERRORCLASS_D2C() {
    return 'D2C API';
  }

  static get BUGSNAG_ERRORCLASS_LOCATIONS() {
    return 'Locations API';
  }

  static get BUGSNAG_ERRORCLASS_API_MIGRATION() {
    return 'API Migration';
  }

  static get BUGSNAG_ERRORCLASS_OKTA() {
    return 'Okta';
  }

  static get AMPLITUDE_EVENT_NAMES() {
    return {
      SHOW_STORE_INFO: '[Show Store Info]',
      OVERRIDE_GUEST_DETAILS: '[Override Guest Details]',
    };
  }

  static get EMPTY_PHONE_FIELD_WARNING_HEADING() {
    return 'Please add a Phone Number for the Delivery Point of Contact';
  }

  static get EMPTY_PHONE_FIELD_WARNING_MESSAGE() {
    return 'CFA One Account phone number field is blank. Please enter phone number in Special Intructions Field';
  }

  static get UNLOCK_BANNER() {
    return {
      HEADER: 'Unlock to update Guest details',
      MEMBER_BODY:
        'Removing Chick-fil-A One account will remove all points for this order and any payment selections connected to the account. Editing will require the order to be resubmitted under new payment. Once submitted, this action cannot be undone.',
      GUEST_BODY:
        'Editing will require the order to be resubmitted under new payment. Once submitted, this action cannot be undone.',
    };
  }

  static get PAPER_GOODS_MESSAGES() {
    return [
      'Please include plates, cutlery, napkins and cups.',
      'Please do not include plates, cutlery, napkins or cups.',
      'Please include plates',
      'Please include cutlery',
      'Please include napkins',
      'Please include cups',
    ];
  }

  static get PAPER_GOODS_MENU_TAG() {
    return 'CUTLERY_CATERING';
  }

  static get PAPER_GOODS_MAX_QUANTITY() {
    return 999;
  }

  static get MAX_PROMO_FREE_ITEM_QTY() {
    return 75;
  }

  static get PROMO_FREE_ITEM_PRICE() {
    return 0.0;
  }

  static get ALL_DAY_CHICKEN_MINI_LOCATIONS() {
    return [
      '02281',
      '02979',
      '02479',
      '03688',
      '03382',
      '02092',
      '04149',
      '03126',
      '03466',
      '03116',
      '04404',
      '04051',
      '01939',
      '02730',
      '03791',
      '03617',
      '02805',
      '02043',
      '03071',
      '03098',
      '01244',
      '04042',
      '03544',
      '00791',
      '04182',
      '02681',
      '02817',
      '01648',
      '04343',
      '03717',
      '03644',
      '01424',
      '04082',
      '00498',
      '70045',
      '03032',
      '03516',
      '03336',
      '02891',
      '04046',
      '03318',
      '01018',
      '02971',
      '01999',
      '04386',
      '02811',
    ];
  }

  static get LAB_204_LOCATION(): LocationSummary {
    return { locationNumber: '90204', name: 'Staging Lab 204', status: 'open' };
  }

  static get SPECIAL_LOCATION_STORE_INFO_MESSAGES() {
    return {
      '02891': 'Transfer all catering questions to Catering Leaders at (304) 982-8382',
      '04302': 'Transfer all catering questions to Catering Leaders at (304) 932-0742',
    };
  }

  static get HARDCODED_PHONE_FOR_03606() {
    return '402-885-8908';
  }

  static get HARDCODED_PHONE_FOR_02891() {
    return '304-982-8382';
  }

  static get HARDCODED_PHONE_FOR_04302() {
    return '304-932-0742';
  }

  static get HARDCODED_PHONE_FOR_03724() {
    return '301-509-3605';
  }

  static get MAX_NUM_OF_DISPLAYED_LOCATIONS() {
    return 7;
  }

  static get PHONE_REGEX() {
    return /^(\+1\s?)?(\(\d{3}\)\s?\d{3}[-\s]?\d{4}|\d{3}[-\s]?\d{3}[-\s]?\d{4})$/i;
  }

  static get EMAIL_REGEX() {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  }

  static get NAME_REGEX() {
    return /^[a-zA-Z](?:[a-zA-Z\- ']*[a-zA-Z])?$/;
  }

  static get TIME_SLOT_INTERVAL_IN_MIN(): NearestMinutes {
    return 15;
  }

  /** Full addMessage payloads with stable ids for de-dupe. */
  static get DIAGNOSTIC_MESSAGES() {
    return {
      REMOVED_CART_ITEMS: {
        id: 'removed-cart-items',
        message: {
          type: DiagnosticMessageType.warning,
          text: 'Items removed from the cart',
          dismissible: true,
        },
      },
      TIMEZONE_MISMATCH: {
        id: 'timezone-mismatch',
        message: {
          type: DiagnosticMessageType.warning,
          text: "Your timezone does not match the Restaurant timezone. Please note all dates and times in CMT use the selected Restaurant's time zone",
          dismissible: true,
        },
      },
      PAPER_GOODS_RESELECT_FROM_MENU: {
        id: 'paper-goods-reselect-from-menu',
        message: {
          type: DiagnosticMessageType.warning,
          text: 'Please reselect Paper Goods in Menu',
          dismissible: true,
        },
      },
      PAPER_GOODS_MAKE_SELECTIONS_FROM_MENU: {
        id: 'paper-goods-make-selections-from-menu',
        message: {
          type: DiagnosticMessageType.warning,
          text: 'Please make your Paper Goods selections from Menu when editing this order',
          dismissible: true,
        },
      },
      PAPER_GOODS_NOT_ADDED_READD_OR_SI: {
        id: 'paper-goods-not-added-readd-or-si',
        message: {
          type: DiagnosticMessageType.warning,
          text: 'Some Paper Goods items from your order were not added to your cart. Please re-add them from the Menu or Special Instructions',
          dismissible: true,
        },
      },
    };
  }

  static get ORIGINAL_ORDER_CANCEL_FAILED() {
    return 'ORIGINAL_ORDER_CANCEL_FAILED';
  }

  static get ORIGINAL_AND_EDITED_ORDER_CANCEL_FAILED() {
    return 'ORIGINAL_AND_EDITED_ORDER_CANCEL_FAILED';
  }
}
